/** Status devolvidos pelo backend para cada tarefa sugerida. */
export const MAINTENANCE_STATUS_OVERDUE = 'atrasada';
export const MAINTENANCE_STATUS_DUE_SOON = 'proxima';
export const MAINTENANCE_STATUS_OK = 'ok';

const STATUS_SEVERITY = {
    [MAINTENANCE_STATUS_OVERDUE]: 0,
    [MAINTENANCE_STATUS_DUE_SOON]: 1,
    [MAINTENANCE_STATUS_OK]: 2,
};

/** Cor do MUI para cada status, usada em chips, bordas e ícones. */
export function maintenanceStatusColor(status) {
    switch (status) {
        case MAINTENANCE_STATUS_OVERDUE:
            return 'error';
        case MAINTENANCE_STATUS_DUE_SOON:
            return 'warning';
        default:
            return 'success';
    }
}

export function maintenanceStatusLabel(status) {
    switch (status) {
        case MAINTENANCE_STATUS_OVERDUE:
            return 'Atrasada';
        case MAINTENANCE_STATUS_DUE_SOON:
            return 'Próxima';
        default:
            return 'Em dia';
    }
}

/**
 * Prazo em texto. Junta os dois relógios (tempo e horas de impressão) em uma
 * frase só, porque o usuário quer saber o que vence primeiro, não fazer a conta.
 */
export function maintenanceDeadlineLabel(task) {
    if (!task) {
        return '';
    }
    const parts = [];
    const days = Number(task.days_remaining) || 0;
    const hours = Number(task.hours_remaining) || 0;

    if (task.interval_days > 0) {
        if (days < 0) {
            parts.push(`${Math.abs(days)} ${pluralize(Math.abs(days), 'dia', 'dias')} de atraso`);
        } else if (days === 0) {
            parts.push('vence hoje');
        } else {
            parts.push(`em ${days} ${pluralize(days, 'dia', 'dias')}`);
        }
    }

    if (task.interval_hours > 0) {
        if (hours < 0) {
            parts.push(`${Math.abs(hours)} h além do intervalo`);
        } else {
            parts.push(`${hours} h de impressão`);
        }
    }

    return parts.join(' · ');
}

/** Intervalo recomendado, do jeito que o plano do modelo define. */
export function maintenanceIntervalLabel(task) {
    if (!task) {
        return '';
    }
    const parts = [];
    if (task.interval_hours > 0) {
        parts.push(`${task.interval_hours} h de impressão`);
    }
    if (task.interval_days > 0) {
        parts.push(`${task.interval_days} ${pluralize(task.interval_days, 'dia', 'dias')}`);
    }
    if (parts.length === 0) {
        return '';
    }
    // "O que vier primeiro" só faz sentido quando existem os dois relógios.
    if (parts.length === 1) {
        return `A cada ${parts[0]}`;
    }
    return `A cada ${parts.join(' ou ')}, o que vier primeiro`;
}

/** Tarefas mais urgentes primeiro, mantendo a ordem do backend nos empates. */
export function sortTasksByUrgency(tasks) {
    return [...(tasks || [])].sort((a, b) => {
        const bySeverity = severityOf(a) - severityOf(b);
        if (bySeverity !== 0) {
            return bySeverity;
        }
        return remainingRatio(a) - remainingRatio(b);
    });
}

/** Janela de antecedência do painel: uma semana é o horizonte de quem planeja a farm. */
export const DUE_SOON_WINDOW_DAYS = 7;

/**
 * Tarefa que pede ação agora: já venceu, vence na próxima semana ou está no fim
 * do intervalo em horas de impressão. O que está em dia fica de fora, porque um
 * painel cheio de "em dia" esconde justamente o que precisa de atenção.
 *
 * O relógio de horas entra mesmo com data distante: se faltam poucas horas de
 * impressão, o vencimento chega pelo uso, não pelo calendário.
 */
export function isActionableTask(task) {
    if (isSnoozedTask(task)) {
        return false;
    }
    if (task?.status === MAINTENANCE_STATUS_OVERDUE) {
        return true;
    }
    if (task?.status !== MAINTENANCE_STATUS_DUE_SOON) {
        return false;
    }
    if (task.due_by === 'horas' || task.due_by === 'ambos') {
        return true;
    }
    return task.interval_days > 0 && (Number(task.days_remaining) || 0) <= DUE_SOON_WINDOW_DAYS;
}

/** Prazos oferecidos para silenciar, em dias. */
export const SNOOZE_DAY_OPTIONS = [1, 3, 5, 10, 15];

/**
 * Tarefa silenciada pelo usuário. O backend só devolve o prazo enquanto ele
 * está valendo, então a presença do campo já basta.
 */
export function isSnoozedTask(task) {
    return Boolean(task?.snoozed_until);
}

/** Só o que pede ação, na ordem de urgência. */
export function pendingTasks(tasks) {
    return (tasks || []).filter(isActionableTask);
}

/**
 * Silenciadas que já apareceriam no painel se não estivessem caladas. Silêncio
 * em tarefa que ainda está em dia não interessa listar: não esconde nada.
 */
export function snoozedTasks(tasks) {
    return (tasks || []).filter((task) => (
        isSnoozedTask(task) && isActionableTask({ ...task, snoozed_until: null })
    ));
}

/** Impressoras com alguma tarefa silenciada, para a lista dos silenciados. */
export function printersWithSnoozedTasks(printers) {
    return (printers || [])
        .map((printer) => ({ ...printer, tasks: sortTasksByUrgency(snoozedTasks(printer.tasks)) }))
        .filter((printer) => printer.tasks.length > 0);
}

/** Impressoras que têm alguma pendência, já ordenadas pela mais urgente. */
export function printersWithPendingTasks(printers) {
    return (printers || [])
        .map((printer) => ({ ...printer, tasks: sortTasksByUrgency(pendingTasks(printer.tasks)) }))
        .filter((printer) => printer.tasks.length > 0);
}

/** Contagem do que está visível, que é o que o cabeçalho do painel resume. */
export function countByStatus(printers) {
    return (printers || []).reduce((totals, printer) => {
        (printer.tasks || []).forEach((task) => {
            if (task.status === MAINTENANCE_STATUS_OVERDUE) {
                totals.overdue += 1;
            } else {
                totals.dueSoon += 1;
            }
        });
        return totals;
    }, { overdue: 0, dueSoon: 0 });
}

function severityOf(task) {
    const severity = STATUS_SEVERITY[task?.status];
    return severity == null ? STATUS_SEVERITY[MAINTENANCE_STATUS_OK] : severity;
}

function remainingRatio(task) {
    let ratio = Number.POSITIVE_INFINITY;
    if (task?.interval_days > 0) {
        ratio = Math.min(ratio, (Number(task.days_remaining) || 0) / task.interval_days);
    }
    if (task?.interval_hours > 0) {
        ratio = Math.min(ratio, (Number(task.hours_remaining) || 0) / task.interval_hours);
    }
    return ratio;
}

function pluralize(value, singular, plural) {
    return Math.abs(value) === 1 ? singular : plural;
}
